import { MediaUpload } from '@/app/shared/models/media/media-upload.model'
import { Media } from '@/app/shared/models/media/media.model'
import { MediaApiService } from '@/app/shared/services/api/media/media.api.service'
import { saveBlobResponse } from '@/app/shared/utils/download.util'
import { environment } from '@/environments/environment'
import { HttpClient, HttpResponse } from '@angular/common/http'
import { DestroyRef, inject, Injectable } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { catchError, map, Observable, of, tap } from 'rxjs'
import { MessageService } from 'primeng/api'

@Injectable({ providedIn: 'root' })
export class MediaService {
  private apiBase = environment.apiBasePath
  private http = inject(HttpClient)
  private messageService = inject(MessageService)
  private destroyRef = inject(DestroyRef)
  private mediaApiService = inject(MediaApiService)

  /** Télécharge depuis une URL */
  getBlob(contentUrl: string): Observable<HttpResponse<Blob>> {
    const url = `${this.apiBase}${contentUrl}`
    return this.http.get(url, {
      observe: 'response',
      responseType: 'blob',
    })
  }

  postBlob(
    contentUrl: string,
    body: Record<string, unknown>,
  ): Observable<HttpResponse<Blob>> {
    const url = `${this.apiBase}${contentUrl}`
    return this.http.post(url, body, {
      observe: 'response',
      responseType: 'blob',
    })
  }

  getBlobUrl(contentUrl: string): Observable<string | null> {
    return this.getBlob(contentUrl!).pipe(
      takeUntilDestroyed(this.destroyRef),
      map((response: HttpResponse<Blob>) => {
        const blob = response.body
        return blob ? URL.createObjectURL(blob) : null
      }),
      catchError(() => {
        this.messageService.add({
          severity: 'error',
          summary: $localize`Erreur`,
          detail: $localize`Impossible de télécharger le fichier`,
        })
        return of(null)
      }),
    )
  }

  download(
    contentUrl: string,
    preferredFilename?: string,
    onComplete?: () => void,
  ) {
    if (!contentUrl) return

    const obs = this.getBlob(contentUrl!)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: HttpResponse<Blob>) => {
        saveBlobResponse(response, preferredFilename)
        if (onComplete) onComplete()
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: $localize`Erreur`,
          detail: $localize`Impossible de télécharger le fichier`,
        })
      },
    })
  }

  downloadBetweenDates(
    contentUrl: string,
    body: Record<string, unknown>,
    preferredFilename?: string,
  ) {
    if (!contentUrl) return

    const obs = this.postBlob(contentUrl!, body)

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: HttpResponse<Blob>) => {
        saveBlobResponse(response, preferredFilename)
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: $localize`Erreur`,
          detail: $localize`Impossible de télécharger le fichier`,
        })
      },
    })
  }

  getPdfUrl(id: string | number, resourceName: string): string {
    switch (resourceName) {
      case 'order':
        return `/api/orders/${id}/pdf`
      case 'medical_report':
        return `/api/patient_infos/${id}/medical_report_pdf`
      case 'transfer_request':
        return `/api/transfer_request/${id}/download-pdf`
      case 'prescription':
        return `/api/prescription/${id}/download-pdf`
      default:
        return `/api/${resourceName}/${id}/download-pdf`
    }
  }

  getMedia(idOrIri: string | number): Observable<Media> {
    const id =
      typeof idOrIri === 'string' && idOrIri.includes('/')
        ? idOrIri.split('/').pop()!
        : idOrIri
    return this.mediaApiService.getById(id, {
      headers: {
        Accept: 'application/ld+json',
      },
    })
  }

  uploadMedia(mediaUpload: MediaUpload): Observable<Media | null> {
    return this.mediaApiService.createMedia(mediaUpload).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(),
      catchError((error) => {
        console.error('errorMedia', error)
        return of(null)
      }),
    )
  }
}
